import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';
import {
  CheckCircleIcon,
  ClockIcon,
  FlagIcon,
  FolderIcon,
  InboxIcon,
  PhoneIcon,
  BookOpenIcon,
  UsersIcon,
  BriefcaseIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';

type TabType = 'tasks' | 'projects' | 'goals' | 'emails' | 'meetings' | 'journal' | 'people' | 'businesses' | 'recordings';

interface RelatedItemsTabsProps {
  tasks?: any[];
  projects?: any[];
  goals?: any[];
  emails?: any[];
  meetings?: any[];
  journalEntries?: any[];
  people?: any[];
  businesses?: any[];
  recordings?: any[];
  onTaskToggle?: (taskId: number, currentStatus: boolean) => void;
  onEmailStatusChange?: (emailId: string, isHandled: boolean) => void;
  onJournalEntryClick?: (entry: any) => void;
  isUpdatingTask?: number | null;
  isUpdatingEmail?: string | null;
}

const RelatedItemsTabs: React.FC<RelatedItemsTabsProps> = ({
  tasks = [],
  projects = [],
  goals = [],
  emails = [],
  meetings = [],
  journalEntries = [],
  people = [],
  businesses = [],
  recordings = [],
  onTaskToggle,
  onEmailStatusChange,
  onJournalEntryClick,
  isUpdatingTask,
  isUpdatingEmail,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('tasks');
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);

  const allTabs = [
    { key: 'tasks' as TabType, label: 'Tasks', icon: CheckCircleIcon, count: tasks.length, data: tasks },
    { key: 'projects' as TabType, label: 'Projects', icon: FolderIcon, count: projects.length, data: projects },
    { key: 'goals' as TabType, label: 'Goals', icon: FlagIcon, count: goals.length, data: goals },
    { key: 'emails' as TabType, label: 'Emails', icon: InboxIcon, count: emails.length, data: emails },
    { key: 'meetings' as TabType, label: 'Meetings', icon: PhoneIcon, count: meetings.length, data: meetings },
    { key: 'journal' as TabType, label: 'Journal', icon: BookOpenIcon, count: journalEntries.length, data: journalEntries },
    { key: 'people' as TabType, label: 'People', icon: UsersIcon, count: people.length, data: people },
    { key: 'businesses' as TabType, label: 'Businesses', icon: BriefcaseIcon, count: businesses.length, data: businesses },
    { key: 'recordings' as TabType, label: 'Recordings', icon: VideoCameraIcon, count: recordings.length, data: recordings },
  ];

  const tabs = allTabs.filter(tab => tab.count > 0); // Only show tabs with data

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return null;
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return null;
    try {
      return format(parseISO(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return 'Invalid Date';
    }
  };

  const itemCardStyle = (itemKey: string): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: SPACING[12],
    padding: SPACING[12],
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.border.default}`,
    backgroundColor: hoveredItem === itemKey ? COLORS.bg.hover : COLORS.bg.panel,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  });

  const itemCardStyleNoFlex = (itemKey: string): React.CSSProperties => ({
    padding: SPACING[12],
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.border.default}`,
    backgroundColor: hoveredItem === itemKey ? COLORS.bg.hover : COLORS.bg.panel,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  });

  const iconStyle = (color: string): React.CSSProperties => ({
    width: '20px',
    height: '20px',
    color: color,
    flexShrink: 0,
    marginTop: '2px',
  });

  const smallIconStyle: React.CSSProperties = {
    width: '16px',
    height: '16px',
    marginRight: SPACING[8],
  };

  const textPrimaryStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.size.sm,
    color: COLORS.text.primary,
  };

  const textSecondaryStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.size.xs,
    color: COLORS.text.secondary,
    marginTop: SPACING[4],
  };

  const badgeStyle = (bgColor: string, textColor: string): React.CSSProperties => ({
    display: 'inline-block',
    marginTop: SPACING[4],
    padding: `2px ${SPACING[8]}`,
    fontSize: TYPOGRAPHY.size.xs,
    borderRadius: RADIUS,
    backgroundColor: bgColor,
    color: textColor,
  });

  const listContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: SPACING[8],
  };

  const renderTasks = () => (
    <div style={listContainerStyle}>
      {tasks.map((task) => (
        <div
          key={task.id}
          onClick={() => navigate(`/tasks/${task.id}`)}
          onMouseEnter={() => setHoveredItem(`task-${task.id}`)}
          onMouseLeave={() => setHoveredItem(null)}
          style={itemCardStyle(`task-${task.id}`)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onTaskToggle) onTaskToggle(task.id, !!task.is_done);
            }}
            disabled={isUpdatingTask === task.id}
            style={{ flexShrink: 0, marginTop: '2px', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            {task.is_done ? (
              <CheckCircleIcon style={{ ...iconStyle(COLORS.status.success), marginTop: 0 }} />
            ) : (
              <ClockIcon style={{ ...iconStyle(COLORS.text.tertiary), marginTop: 0 }} />
            )}
          </button>
          <div style={{ flex: 1 }}>
            <p style={{
              fontSize: TYPOGRAPHY.size.sm,
              color: task.is_done ? COLORS.text.secondary : COLORS.text.primary,
              textDecoration: task.is_done ? 'line-through' : 'none',
            }}>
              {task.description || task.title}
            </p>
            {task.due_date && (
              <p style={textSecondaryStyle}>Due: {formatDate(task.due_date)}</p>
            )}
            {task.status && (
              <span style={badgeStyle(COLORS.bg.hover, COLORS.text.secondary)}>
                {task.status}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderProjects = () => (
    <div style={listContainerStyle}>
      {projects.map((project) => (
        <div
          key={project.id}
          onClick={() => navigate(`/projects/${project.id}`)}
          onMouseEnter={() => setHoveredItem(`project-${project.id}`)}
          onMouseLeave={() => setHoveredItem(null)}
          style={itemCardStyle(`project-${project.id}`)}
        >
          <FolderIcon style={iconStyle(COLORS.accent.default)} />
          <div style={{ flex: 1 }}>
            <p style={textPrimaryStyle}>{project.name || project.title}</p>
            {project.status && (
              <span style={badgeStyle('#DBEAFE', COLORS.accent.default)}>
                {project.status}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderGoals = () => (
    <div style={listContainerStyle}>
      {goals.map((goal) => (
        <div
          key={goal.id}
          onClick={() => navigate(`/goals/${goal.id}`)}
          onMouseEnter={() => setHoveredItem(`goal-${goal.id}`)}
          onMouseLeave={() => setHoveredItem(null)}
          style={itemCardStyle(`goal-${goal.id}`)}
        >
          <FlagIcon style={iconStyle(COLORS.status.warning)} />
          <div style={{ flex: 1 }}>
            <p style={textPrimaryStyle}>{goal.title}</p>
            {goal.target_date && (
              <p style={textSecondaryStyle}>Target: {formatDate(goal.target_date)}</p>
            )}
            {goal.status && (
              <span style={badgeStyle('#FEF3C7', '#B45309')}>
                {goal.status}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderEmails = () => (
    <div style={listContainerStyle}>
      {emails.map((email) => (
        <div
          key={email.id}
          onMouseEnter={() => setHoveredItem(`email-${email.id}`)}
          onMouseLeave={() => setHoveredItem(null)}
          style={itemCardStyleNoFlex(`email-${email.id}`)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: SPACING[8] }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: TYPOGRAPHY.size.sm, fontWeight: TYPOGRAPHY.weight.medium, color: COLORS.text.primary }}>
                {email.subject}
              </p>
              <p style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary, marginTop: SPACING[4] }}>
                From: {email.sender_name || email.sender}
              </p>
              <p style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary }}>
                {formatDateTime(email.received_at)}
              </p>
            </div>
            {onEmailStatusChange && (
              <button
                onClick={() => onEmailStatusChange(email.id, !email.is_handled)}
                disabled={isUpdatingEmail === email.id}
                style={{
                  padding: `${SPACING[4]} ${SPACING[8]}`,
                  fontSize: TYPOGRAPHY.size.xs,
                  borderRadius: RADIUS,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: email.is_handled ? '#D1FAE5' : COLORS.bg.hover,
                  color: email.is_handled ? COLORS.status.success : COLORS.text.secondary,
                }}
              >
                {email.is_handled ? 'Handled' : 'Mark Handled'}
              </button>
            )}
          </div>
          {email.body && (
            <p style={{
              fontSize: TYPOGRAPHY.size.xs,
              color: COLORS.text.secondary,
              marginTop: SPACING[8],
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}>
              {email.body}
            </p>
          )}
        </div>
      ))}
    </div>
  );

  const renderMeetings = () => (
    <div style={listContainerStyle}>
      {meetings.map((meeting) => (
        <div
          key={meeting.id}
          onClick={() => navigate(`/meetings/${meeting.id}`)}
          onMouseEnter={() => setHoveredItem(`meeting-${meeting.id}`)}
          onMouseLeave={() => setHoveredItem(null)}
          style={itemCardStyle(`meeting-${meeting.id}`)}
        >
          <PhoneIcon style={iconStyle('#A855F7')} />
          <div style={{ flex: 1 }}>
            <p style={textPrimaryStyle}>{meeting.title || meeting.summary}</p>
            {meeting.start_time && (
              <p style={textSecondaryStyle}>{formatDateTime(meeting.start_time)}</p>
            )}
            {meeting.location && (
              <p style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary }}>Location: {meeting.location}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderJournal = () => (
    <div style={listContainerStyle}>
      {journalEntries.map((entry) => (
        <div
          key={entry.id}
          onClick={() => onJournalEntryClick ? onJournalEntryClick(entry) : navigate('/journal')}
          onMouseEnter={() => setHoveredItem(`journal-${entry.id}`)}
          onMouseLeave={() => setHoveredItem(null)}
          style={itemCardStyleNoFlex(`journal-${entry.id}`)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: SPACING[8] }}>
            <p style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary }}>{formatDate(entry.date || entry.created_at)}</p>
            {entry.emotion && (
              <span style={badgeStyle('#E0E7FF', COLORS.status.info)}>
                {entry.emotion}
              </span>
            )}
          </div>
          <p style={{
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.text.primary,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
          }}>
            {entry.content}
          </p>
          <p style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.tertiary, marginTop: SPACING[8], fontStyle: 'italic' }}>
            Click to view and edit
          </p>
        </div>
      ))}
    </div>
  );

  const renderPeople = () => (
    <div style={listContainerStyle}>
      {people.map((person) => (
        <div
          key={person.id}
          onClick={() => navigate(`/people/${person.id}`)}
          onMouseEnter={() => setHoveredItem(`person-${person.id}`)}
          onMouseLeave={() => setHoveredItem(null)}
          style={itemCardStyle(`person-${person.id}`)}
        >
          <UsersIcon style={iconStyle(COLORS.text.secondary)} />
          <div style={{ flex: 1 }}>
            <p style={textPrimaryStyle}>{person.name}</p>
            {person.relationship && (
              <p style={textSecondaryStyle}>{person.relationship}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderBusinesses = () => (
    <div style={listContainerStyle}>
      {businesses.map((business) => (
        <div
          key={business.id}
          onClick={() => navigate(`/businesses/${business.id}`)}
          onMouseEnter={() => setHoveredItem(`business-${business.id}`)}
          onMouseLeave={() => setHoveredItem(null)}
          style={itemCardStyle(`business-${business.id}`)}
        >
          <BriefcaseIcon style={iconStyle(COLORS.text.secondary)} />
          <div style={{ flex: 1 }}>
            <p style={textPrimaryStyle}>{business.name}</p>
            {business.industry && (
              <p style={textSecondaryStyle}>{business.industry}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderRecordings = () => (
    <div style={listContainerStyle}>
      {recordings.map((recording) => (
        <div
          key={recording.id}
          onMouseEnter={() => setHoveredItem(`recording-${recording.id}`)}
          onMouseLeave={() => setHoveredItem(null)}
          style={itemCardStyleNoFlex(`recording-${recording.id}`)}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: SPACING[12] }}>
            <VideoCameraIcon style={iconStyle(COLORS.status.error)} />
            <div style={{ flex: 1 }}>
              <p style={textPrimaryStyle}>{recording.title || 'Untitled Recording'}</p>
              {recording.recorded_at && (
                <p style={textSecondaryStyle}>{formatDateTime(recording.recorded_at)}</p>
              )}
              {recording.duration && (
                <p style={{ fontSize: TYPOGRAPHY.size.xs, color: COLORS.text.secondary }}>Duration: {recording.duration}</p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'tasks':
        return renderTasks();
      case 'projects':
        return renderProjects();
      case 'goals':
        return renderGoals();
      case 'emails':
        return renderEmails();
      case 'meetings':
        return renderMeetings();
      case 'journal':
        return renderJournal();
      case 'people':
        return renderPeople();
      case 'businesses':
        return renderBusinesses();
      case 'recordings':
        return renderRecordings();
      default:
        return (
          <p style={{ fontSize: TYPOGRAPHY.size.sm, color: COLORS.text.secondary, textAlign: 'center', padding: SPACING[32] }}>
            No data available
          </p>
        );
    }
  };

  if (tabs.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: SPACING[32], color: COLORS.text.secondary }}>
        <p>No related items found</p>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: COLORS.bg.panel, borderRadius: RADIUS }}>
      {/* Tabs */}
      <div style={{ borderBottom: `1px solid ${COLORS.border.default}` }}>
        <div style={{ display: 'flex', overflowX: 'auto' }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            const isHovered = hoveredTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                onMouseEnter={() => setHoveredTab(tab.key)}
                onMouseLeave={() => setHoveredTab(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: `${SPACING[12]} ${SPACING[16]}`,
                  fontSize: TYPOGRAPHY.size.sm,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  borderBottom: `2px solid ${isActive ? COLORS.accent.default : 'transparent'}`,
                  whiteSpace: 'nowrap',
                  color: isActive ? COLORS.accent.default : (isHovered ? COLORS.text.primary : COLORS.text.secondary),
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottomWidth: '2px',
                  borderBottomStyle: 'solid',
                  borderBottomColor: isActive ? COLORS.accent.default : (isHovered ? COLORS.border.hover : 'transparent'),
                  cursor: 'pointer',
                  transition: 'color 0.15s ease, border-color 0.15s ease',
                }}
              >
                <Icon style={smallIconStyle} />
                {tab.label}
                <span style={{
                  marginLeft: SPACING[8],
                  padding: `2px ${SPACING[8]}`,
                  fontSize: TYPOGRAPHY.size.xs,
                  borderRadius: '9999px',
                  backgroundColor: COLORS.bg.hover,
                  color: COLORS.text.secondary,
                }}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: SPACING[16] }}>
        {renderContent()}
      </div>
    </div>
  );
};

export default RelatedItemsTabs;
